import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Button } from "@web/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@web/components/ui/card"
import { Checkbox } from "@web/components/ui/checkbox"
import { Input } from "@web/components/ui/input"
import { Label } from "@web/components/ui/label"
import { useToast } from "@web/components/ui/toast"
import { signIn, useSession } from "@web/lib/auth-client"
import { formatCPF } from "@web/lib/utils"
import { useLanguage } from "@web/lib/contexts/language-context"

export function LoginPage() {
	const { t } = useLanguage()

	const [showPassword, setShowPassword] = useState(false)
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [rememberMe, setRememberMe] = useState(false)
	const [errors, setErrors] = useState<{
		email?: string
		password?: string
		general?: string
	}>({})

	const { addToast } = useToast()
	const navigate = useNavigate()
	const location = useLocation()
	const { data: session, isPending } = useSession()

	const from = location.state?.from?.pathname || "/dashboard"

	useEffect(() => {
		if (session?.user && !isPending) navigate(from, { replace: true })
	}, [session, isPending, navigate, from])

	const validateForm = () => {
		const newErrors: { email?: string; password?: string } = {}

		if (!email.trim()) {
			newErrors.email = t.loginPage.errors.emailRequired
		} else if (email.includes("@") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			newErrors.email = t.loginPage.errors.emailInvalid
		} else if (!email.includes("@") && !/^\d{11}$/.test(email.replace(/\D/g, ""))) {
			newErrors.email = t.loginPage.errors.cpfInvalid
		}

		if (!password) {
			newErrors.password = t.loginPage.errors.passwordRequired
		} else if (password.length < 6) {
			newErrors.password = t.loginPage.errors.passwordMinLength
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!validateForm()) {
			return
		}

		setIsLoading(true)
		setErrors({})

		try {
			const result = await signIn.email({
				email,
				password,
				rememberMe,
			})

			if (result.error) {
				setErrors({ general: t.loginPage.errors.invalidCredentials })
				addToast(t.loginPage.toasts.loginError, "error")
			} else {
				addToast(t.loginPage.toasts.loginSuccess, "success")
				navigate(from, { replace: true })
			}
		} catch (error) {
			console.error("Erro ao fazer login:", error)
			setErrors({ general: t.loginPage.errors.serverError })
			addToast(t.loginPage.toasts.serverError, "error")
		} finally {
			setIsLoading(false)
		}
	}

	const _handleSocialLogin = async (provider: "google" | "facebook") => {
		try {
			setIsLoading(true)
			await signIn.social({
				provider,
				callbackURL: window.location.origin + from,
			})
		} catch (error) {
			console.error(`Erro ao fazer login com ${provider}:`, error)
			addToast(`Erro ao fazer login com ${provider}`, "error")
		} finally {
			setIsLoading(false)
		}
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		if (!value.includes("@") && /^\d/.test(value)) {
			setEmail(formatCPF(value))
		} else {
			setEmail(value)
		}
		if (errors.email) {
			setErrors((prev) => ({ ...prev, email: undefined }))
		}
	}

	if (isPending) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
			</div>
		)
	}

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
			<div className="w-full max-w-md space-y-6">
				<Card className="overflow-hidden rounded-xl shadow-xl border-0">
					<CardHeader className="bg-gradient-to-r">
						<CardTitle className="text-center font-bold text-2xl">{t.loginPage.title}</CardTitle>
					</CardHeader>

					<CardContent className="p-6">
						{errors.general && (
							<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
								<AlertCircle className="h-4 w-4" />
								<span className="text-sm">{errors.general}</span>
							</div>
						)}

						<form onSubmit={handleLogin} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">{t.loginPage.fields.email.label}</Label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										id="email"
										type="text"
										placeholder={t.loginPage.fields.email.placeholder}
										className={`pl-10 ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
										value={email}
										onChange={handleEmailChange}
										required
									/>
								</div>
								{errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
							</div>

							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label htmlFor="password">{t.loginPage.fields.password.label}</Label>
									<Link to="/forgot-password" className="text-primary text-xs hover:underline">
										{t.loginPage.links.forgotPassword}
									</Link>
								</div>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
									<Input
										id="password"
										type={showPassword ? "text" : "password"}
										placeholder={t.loginPage.fields.password.placeholder}
										className={`pl-10 pr-10 ${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
										value={password}
										onChange={(e) => {
											setPassword(e.target.value)
											if (errors.password) {
												setErrors((prev) => ({ ...prev, password: undefined }))
											}
										}}
										required
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
										onClick={() => setShowPassword(!showPassword)}>
										{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
										<span className="sr-only">
											{showPassword ? t.loginPage.screenReader.hidePassword : t.loginPage.screenReader.showPassword}
										</span>
									</Button>
								</div>
								{errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="remember"
									checked={rememberMe}
									onCheckedChange={(checked) => setRememberMe(checked as boolean)}
								/>
								<Label htmlFor="remember" className="font-normal text-sm">
									{t.loginPage.rememberMe}
								</Label>
							</div>

							<Button
								type="submit"
								size="lg"
								className="w-full bg-gradient-to-r bg-primary  transition-all duration-200"
								disabled={isLoading}>
								{isLoading ? (
									<span className="flex items-center justify-center">
										<div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
										{t.loginPage.buttons.signingIn}
									</span>
								) : (
									<span className="flex items-center justify-center">
										{t.loginPage.buttons.signIn}
										<ArrowRight className="ml-2 h-4 w-4" />
									</span>
								)}
							</Button>
						</form>

						{/* <div className="relative my-6">
							<Separator />
							<span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-muted-foreground text-xs">
								Ou continue com
							</span>
						</div> */}

						{/* <div className="grid grid-cols-2 gap-4">
							<Button
								variant="outline"
								type="button"
								className="w-full hover:bg-red-50 hover:border-red-200 transition-colors bg-transparent"
								onClick={() => handleSocialLogin("google")}
								disabled={isLoading}
							>
								<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
									<path
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
										fill="#4285F4"
									/>
									<path
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										fill="#34A853"
									/>
									<path
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
										fill="#FBBC05"
									/>
									<path
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
										fill="#EA4335"
									/>
								</svg>
								Google
							</Button>
							<Button
								variant="outline"
								type="button"
								className="w-full hover:bg-blue-50 hover:border-blue-200 transition-colors bg-transparent"
								onClick={() => handleSocialLogin("facebook")}
								disabled={isLoading}
							>
								<svg
									className="mr-2 h-4 w-4"
									fill="#1877F2"
									viewBox="0 0 24 24"
								>
									<path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0014.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" />
								</svg>
								Facebook
							</Button>
						</div> */}
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
